/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * ============================================================================
 * Commissions Service - Super Admin Commissions API
 * ============================================================================
 *
 * Service layer for partner commission management API calls.
 *
 * ============================================================================
 */

import { get, post } from '@/services/api/client';
import { ApiResponse, PaginatedResponse, PaginationParams, SpringPageResponse, transformPageResponse } from '@/services/api/types';
import {
  Commission,
  CommissionSummary,
  CommissionStats,
  CreateCommissionRequest,
  CommissionFilterParams,
  CommissionStatus,
} from '@/types/superadmin.types';

const BASE_URL = '/superadmin/commissions';

/**
 * Backend commission response
 */
interface BackendCommission {
  id: string;
  partnerId: string;
  partnerName?: string;
  paymentId?: string;
  invoiceNumber?: string;
  organizationId?: string;
  organizationName?: string;
  amount: number;
  currency: string;
  commissionRate: number;
  baseAmount: number;
  commissionType: string;
  status: string;
  description?: string;
  billingPeriodStart?: string;
  billingPeriodEnd?: string;
  approvedAt?: string;
  approvedBy?: string;
  paidAt?: string;
  withdrawalId?: string;
  cancellationReason?: string;
  createdAt: string;
  updatedAt?: string;
}

/**
 * Transform backend commission to frontend format
 */
function transformCommission(backend: BackendCommission): Commission {
  return {
    id: backend.id,
    partnerId: backend.partnerId,
    partnerName: backend.partnerName,
    paymentId: backend.paymentId,
    invoiceNumber: backend.invoiceNumber,
    organizationId: backend.organizationId,
    organizationName: backend.organizationName,
    amount: Number(backend.amount),
    currency: backend.currency,
    commissionRate: Number(backend.commissionRate),
    baseAmount: Number(backend.baseAmount),
    commissionType: backend.commissionType as Commission['commissionType'],
    status: backend.status as CommissionStatus,
    description: backend.description,
    billingPeriodStart: backend.billingPeriodStart,
    billingPeriodEnd: backend.billingPeriodEnd,
    approvedAt: backend.approvedAt,
    approvedBy: backend.approvedBy,
    paidAt: backend.paidAt,
    withdrawalId: backend.withdrawalId,
    cancellationReason: backend.cancellationReason,
    createdAt: backend.createdAt,
    updatedAt: backend.updatedAt || backend.createdAt,
  };
}

/**
 * Commissions service for Super Admin
 */
export const commissionsService = {
  /**
   * Get paginated list of all commissions
   */
  async getCommissions(
    params?: PaginationParams & CommissionFilterParams
  ): Promise<PaginatedResponse<Commission>> {
    const { status, partnerId, ...otherParams } = params || {};

    // Build the correct URL based on filters
    let url = BASE_URL;
    if (partnerId && status) {
      url = `${BASE_URL}/partner/${partnerId}/status/${status}`;
    } else if (partnerId) {
      url = `${BASE_URL}/partner/${partnerId}`;
    } else if (status) {
      url = `${BASE_URL}/status/${status}`;
    }

    const response = await get<ApiResponse<SpringPageResponse<BackendCommission>>>(url, otherParams);
    const transformed = transformPageResponse(response.data);
    return {
      ...transformed,
      content: transformed.content.map(transformCommission),
    };
  },

  /**
   * Get commission by ID
   */
  async getCommission(id: string): Promise<Commission> {
    const response = await get<ApiResponse<BackendCommission>>(`${BASE_URL}/${id}`);
    return transformCommission(response.data);
  },

  /**
   * Get commissions by partner
   */
  async getCommissionsByPartner(
    partnerId: string,
    params?: PaginationParams
  ): Promise<PaginatedResponse<Commission>> {
    const response = await get<ApiResponse<SpringPageResponse<BackendCommission>>>(
      `${BASE_URL}/partner/${partnerId}`,
      params
    );
    const transformed = transformPageResponse(response.data);
    return {
      ...transformed,
      content: transformed.content.map(transformCommission),
    };
  },

  /**
   * Get commission summary for a partner
   */
  async getPartnerSummary(partnerId: string): Promise<CommissionSummary> {
    const response = await get<ApiResponse<CommissionSummary>>(`${BASE_URL}/partner/${partnerId}/summary`);
    return response.data;
  },

  /**
   * Get platform-wide commission statistics
   */
  async getStats(): Promise<CommissionStats> {
    const response = await get<ApiResponse<CommissionStats>>(`${BASE_URL}/stats`);
    return response.data;
  },

  /**
   * Create a manual commission
   */
  async createCommission(data: CreateCommissionRequest): Promise<Commission> {
    const response = await post<ApiResponse<BackendCommission>>(BASE_URL, data);
    return transformCommission(response.data);
  },

  /**
   * Approve a single commission
   */
  async approveCommission(id: string): Promise<Commission> {
    const response = await post<ApiResponse<BackendCommission>>(`${BASE_URL}/${id}/approve`);
    return transformCommission(response.data);
  },

  /**
   * Bulk approve commissions
   */
  async bulkApproveCommissions(ids: string[]): Promise<{ approved: number }> {
    const response = await post<ApiResponse<{ approved: number }>>(`${BASE_URL}/approve-bulk`, ids);
    return response.data;
  },

  /**
   * Auto-approve all pending commissions for a partner
   */
  async autoApprovePartnerCommissions(partnerId: string): Promise<{ approved: number }> {
    const response = await post<ApiResponse<{ approved: number }>>(
      `${BASE_URL}/partner/${partnerId}/approve-all`
    );
    return response.data;
  },

  /**
   * Cancel a commission
   */
  async cancelCommission(id: string, reason?: string): Promise<Commission> {
    const response = await post<ApiResponse<BackendCommission>>(`${BASE_URL}/${id}/cancel`, { reason });
    return transformCommission(response.data);
  },

  /**
   * Reverse a paid commission
   */
  async reverseCommission(id: string, reason?: string): Promise<Commission> {
    const response = await post<ApiResponse<BackendCommission>>(`${BASE_URL}/${id}/reverse`, { reason });
    return transformCommission(response.data);
  },
};

export default commissionsService;
